import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  traceabilityApi,
  type LotHit,
  type Origin,
  type OriginKind,
  type OriginNode,
  type Usage,
} from "../../api/traceability";
import { useAuth } from "../../auth/AuthContext";
import { KPI } from "../../components/primitives.jsx";
import { Select } from "../../components/Select";
import { LotQualityBadge } from "../../components/LotQualityBadge";
import { formatDate, formatNumber, intlLocale } from "../../lib/format";

/**
 * Sledljivost jednog LOT-a, u oba smera.
 *
 * Dva pitanja koja se postavljaju kad nešto krene naopako, i oba moraju da budu na istom ekranu:
 * „od čega je ovo napravljeno" i „ko je sve dobio robu od ove sirovine". Prvo se gleda kad kupac
 * prijavi nalaz, drugo kad se roba povlači — i tad se broji u minutima.
 */
export function TraceabilityPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const [param, setParam] = useSearchParams();
  // The customer card is a sales screen. Quality staff who trace a lot may not hold it, and for them
  // the name stays plain text rather than a link that ends on "forbidden".
  const { hasPermission } = useAuth();
  const mozeFirmu = hasPermission("section:companies:read");

  const lotId = param.get("lot");
  const [upit, setUpit] = useState(param.get("q") ?? "");
  const [nalazi, setNalazi] = useState<LotHit[] | null>(null);
  // Odsečen spisak mora da kaže da je odsečen: tih spisak od pedeset redova čitalac čita kao
  // „toliko ih ima" i dalje traži po onome što je već odsečeno (pravilo 15).
  const [odseceno, setOdseceno] = useState(0);
  // Neuspeh poziva se NE sme prikazati kao „nema rezultata": tako je udvojena putanja u API-ju
  // izgledala kao da LOT ne postoji, iako je postojao.
  const [pretragaPukla, setPretragaPukla] = useState(false);
  const [dubina, setDubina] = useState(4);
  const [poreklo, setPoreklo] = useState<Origin | null>(null);
  const [upotreba, setUpotreba] = useState<Usage | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [ucitava, setUcitava] = useState(false);

  // Pretraga ide posle kratke pauze, da svaki pritisak tastera ne bude upit.
  const pauza = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(pauza.current);
    if (upit.trim().length < 2) { setNalazi(null); return; }
    pauza.current = window.setTimeout(() => {
      setPretragaPukla(false);
      traceabilityApi.search(upit.trim())
        .then((r) => { setNalazi(r.rows); setOdseceno(r.cut ? r.totalAtMost : 0); })
        .catch(() => { setNalazi(null); setPretragaPukla(true); });
    }, 250);
    return () => window.clearTimeout(pauza.current);
  }, [upit]);

  useEffect(() => {
    if (!lotId) { setPoreklo(null); setUpotreba(null); return; }
    let otkazano = false;
    setUcitava(true);
    setGreska(null);
    Promise.all([
      traceabilityApi.origin(lotId, dubina),
      traceabilityApi.usage(lotId, dubina),
    ])
      .then(([p, u]) => { if (!otkazano) { setPoreklo(p); setUpotreba(u); } })
      .catch(() => { if (!otkazano) setGreska(t("trace.error", "LOT nije nađen ili nemate pravo pristupa.")); })
      .finally(() => { if (!otkazano) setUcitava(false); });
    return () => { otkazano = true; };
  }, [lotId, dubina, t]);

  const izaberi = useCallback((id: string) => {
    setParam({ lot: id }, { replace: false });
    setNalazi(null);
  }, [setParam]);

  const kolicina = (n: number | null, jed: string | null) =>
    n == null ? "—" : `${formatNumber(n, locale, { maximumFractionDigits: 2 })}${jed ? ` ${jed}` : ""}`;

  const glava = poreklo?.lot ?? upotreba?.lot ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 20 }}>{t("trace.title", "Sledljivost LOT-a")}</h1>
        <p style={{ margin: "6px 0 0", color: "var(--ink-3)", fontSize: 13, maxWidth: 680 }}>
          {t("trace.lead",
             "Unesite oznaku LOT-a sa deklaracije. Nalaz pokazuje od čega je roba napravljena i ko je sve dobio robu iz tog LOT-a.")}
        </p>
      </header>

      <div style={traka}>
        <input
          id="trace-upit"
          value={upit}
          onChange={(e) => setUpit(e.target.value)}
          placeholder={t("trace.search", "Oznaka LOT-a, npr. 210926H1A3431")}
          style={polje}
        />
        <Select
          value={String(dubina)}
          onChange={(v) => setDubina(Number(v))}
          options={[1, 2, 3, 4, 6, 8].map((d) => ({
            value: String(d),
            label: t("trace.depth", "{{count}} koraka", { count: d }),
          }))}
        />
      </div>

      {nalazi != null && nalazi.length > 0 && (
        <ul style={spisakNalaza}>
          {odseceno > 0 && (
            <div className="sub" style={{ padding: "6px 12px" }}>
              {t("lotSearch.cut", "Prikazano prvih {{n}} od {{svih}} šarži — suzite pretragu.", {
                n: nalazi.length, svih: odseceno,
              })}
            </div>
          )}
          {nalazi.map((h) => (
            <li key={h.id}>
              <button type="button" onClick={() => izaberi(h.id)} style={nalaz}>
                {/* The ERP barcode names the lot; the designation of intake soy carries its class. */}
                <b style={{ fontVariantNumeric: "tabular-nums" }}>{h.barcode ?? h.designation ?? "—"}</b>
                {h.qualityStatus && <LotQualityBadge status={h.qualityStatus} />}
                <span style={{ color: "var(--ink-3)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {h.productName ?? "—"}
                </span>
                <span style={{ color: "var(--ink-4)", flexShrink: 0 }}>
                  {h.producedOn ? formatDate(h.producedOn) : "—"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {nalazi != null && nalazi.length === 0 && upit.trim().length >= 2 && (
        <p style={tiho}>{t("trace.noHits", "Nema LOT-a sa tom oznakom.")}</p>
      )}
      {pretragaPukla && (
        <p style={{ ...tiho, color: "var(--err)" }}>
          {t("trace.searchFailed", "Pretraga nije uspela. Ovo nije isto što i „nema LOT-a“ — pokušajte ponovo.")}
        </p>
      )}

      {greska && <p style={{ ...tiho, color: "var(--err)" }}>{greska}</p>}
      {ucitava && <p style={tiho}>{t("common.loading", "Učitavanje…")}</p>}

      {glava && (
        <section style={kartica}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "baseline" }}>
            <b style={{ fontSize: 18, fontVariantNumeric: "tabular-nums" }}>{glava.designation ?? "—"}</b>
            <LotQualityBadge status={glava.qualityStatus} note={glava.qualityNote} />
            <span style={{ color: "var(--ink-2)" }}>{glava.productName ?? "—"}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, fontSize: 12.5, color: "var(--ink-3)" }}>
            <span>{t("trace.produced", "Proizveden")}: <b style={{ color: "var(--ink-2)" }}>
              {glava.producedOn ? formatDate(glava.producedOn) : "—"}</b></span>
            <span>{t("trace.expires", "Ističe")}: <b style={{ color: "var(--ink-2)" }}>
              {glava.expiresOn ? formatDate(glava.expiresOn) : "—"}</b></span>
            {glava.erpProductId && <span>{t("trace.article", "Artikal")}: <b style={{ color: "var(--ink-2)" }}>
              {glava.erpProductId}</b></span>}
          </div>
        </section>
      )}

      {poreklo && (
        <Odeljak
          naslov={t("trace.origin.title", "Od čega je napravljen")}
          podnaslov={t("trace.origin.lead",
            "Korak nazad, kroz recepturu radnog naloga. Prepisano sa drugog LOT-a nije isto što i napravljeno od sirovine, pa je i označeno drugačije.")}
        >
          {!poreklo.recorded ? (
            <p style={tiho}>
              {/* Says what the imported orders hold, not what the ERP lacks: the portal only knows the
                  orders it imported, and a lot without a record there may still have an origin. */}
              {t("trace.origin.notRecorded", "U uvezenim nalozima nema zapisa o poreklu ovog LOT-a.")}
            </p>
          ) : (
            <>
              {poreklo.truncated && (
                <p style={{ ...tiho, color: "var(--warn)" }}>
                  {t("trace.truncated",
                     "Prikazan je deo spiska: nalaz je prešao granicu prikaza. Smanjite broj koraka za uži odgovor.")}
                </p>
              )}
              <Stablo koren={poreklo.lot.id} cvorovi={poreklo.nodes} locale={locale} t={t} />
              {/* Padajući izbor dubine izgleda pokvareno kad sirovine nemaju svoje poreklo: menjaš
                  broj koraka a ništa se ne dešava. Bolje je reći zašto. */}
              {poreklo.nodes.every((n) => n.level === 1) && dubina > 1 && (
                <p style={{ ...tiho, margin: 0 }}>
                  {t("trace.origin.noDeeper",
                     "Sirovine nemaju upisano svoje poreklo, pa veći broj koraka ne pokazuje više.")}
                </p>
              )}
            </>
          )}
        </Odeljak>
      )}

      {upotreba && (
        <Odeljak
          naslov={t("trace.usage.title", "Ko je dobio robu iz ovog LOT-a")}
          podnaslov={t("trace.usage.lead",
            "Korak napred. Ovo je spisak koji se zove kad se roba povlači.")}
        >
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <KPI label={t("trace.usage.customers", "Kupaca")}
                 value={String(upotreba.companies.length)}
                 delta={undefined} trend={undefined} icon={undefined} />
            {/* Distinct dispatch notes: one note can carry several lots of the same tree. */}
            <KPI label={t("trace.usage.notes", "Otpremnica")}
                 value={String(new Set(upotreba.deliveries.map((d) => d.noteId)).size)}
                 delta={undefined} trend={undefined} icon={undefined} />
          </div>

          {/* The dispatch notes are a copy of the ERP up to a day; past it nothing is known, so an
              empty list says up to when it is empty. */}
          {upotreba.companies.length === 0 ? (
            <p style={tiho}>
              {upotreba.coversTo
                ? t("trace.usage.noneUntil", "Do {{datum}} roba iz ovog LOT-a nije otpremljena kupcu.",
                    { datum: formatDate(upotreba.coversTo) })
                : t("trace.usage.none", "Roba iz ovog LOT-a nije ni jednom otpremljena kupcu.")}
            </p>
          ) : (
            <>
              {upotreba.coversTo && (
                <p style={tiho}>
                  {t("trace.usage.coversTo", "Otpremnice iz ERP-a do {{datum}}.",
                     { datum: formatDate(upotreba.coversTo) })}
                </p>
              )}
              {upotreba.truncated && (
                <p style={{ ...tiho, color: "var(--warn)" }}>
                  {t("trace.truncated",
                     "Prikazan je deo spiska: nalaz je prešao granicu prikaza. Smanjite broj koraka za uži odgovor.")}
                </p>
              )}
              <table style={tabela}>
                <thead>
                  <tr>
                    <th style={th}>{t("trace.usage.customer", "Kupac")}</th>
                    <th style={thD}>{t("trace.usage.quantity", "Količina")}</th>
                    <th style={thD}>{t("trace.usage.count", "Otpremnica")}</th>
                    <th style={th}>{t("trace.usage.period", "Period")}</th>
                    <th style={thD}>{t("trace.usage.steps", "Koraka")}</th>
                  </tr>
                </thead>
                <tbody>
                  {upotreba.companies.map((c) => (
                    <tr key={c.companyId}>
                      <td style={td}>
                        {mozeFirmu
                          ? <Link to={`/admin/companies/${c.companyId}`} style={veza}>{c.companyName}</Link>
                          : c.companyName}
                      </td>
                      <td style={tdD}>{kolicina(c.quantity, c.unit)}</td>
                      <td style={tdD}>{c.deliveries}</td>
                      <td style={{ ...td, color: "var(--ink-3)", fontSize: 12.5 }}>
                        {c.firstShipped ? formatDate(c.firstShipped) : "—"}
                        {c.lastShipped && c.lastShipped !== c.firstShipped
                          ? ` – ${formatDate(c.lastShipped)}` : ""}
                      </td>
                      {/* Nula koraka znači da je sama ta roba otpremljena, bez prerade. */}
                      <td style={tdD}>{c.minSteps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {/* A transfer to Bankom's own retail store or an internal order moves goods between Bankom's
              warehouses and has no recipient, so the recall list does not hold it. The list says so. */}
          <p style={tiho}>
            {t("trace.usage.scope",
               "Spisak obuhvata samo otpremnice kupcima, a ne prenose u maloprodaju i interne naloge.")}
          </p>
        </Odeljak>
      )}

      {!glava && !ucitava && !greska && (
        <p style={tiho}>
          {t("trace.empty", "Izaberite LOT iz pretrage da bi se prikazala sledljivost.")}
        </p>
      )}
    </div>
  );
}

/**
 * Stablo porekla.
 *
 * Crta se iz ravnog spiska, grupisanjem po LOT-u na čijoj receptura red stoji. Dubina se ne računa
 * iz nivoa nego iz stvarnog puta, da red koji se u podacima javlja na dva mesta ne iskoči iz uvlake.
 */
function Stablo({
  koren, cvorovi, locale, t,
}: {
  koren: string;
  cvorovi: OriginNode[];
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  const poRoditelju = useMemo(() => {
    const m = new Map<string, OriginNode[]>();
    for (const c of cvorovi) {
      const lista = m.get(c.parentLotId);
      if (lista) lista.push(c);
      else m.set(c.parentLotId, [c]);
    }
    return m;
  }, [cvorovi]);

  const redovi = useMemo(() => {
    const out: Array<{ cvor: OriginNode; uvlaka: number }> = [];
    // Obilazak u dubinu, sa setom na putu: isti LOT u krugu se ne crta drugi put.
    const hodaj = (lot: string, uvlaka: number, naPutu: Set<string>) => {
      if (uvlaka > 8) return;
      for (const c of poRoditelju.get(lot) ?? []) {
        if (naPutu.has(c.lotId)) continue;
        out.push({ cvor: c, uvlaka });
        naPutu.add(c.lotId);
        hodaj(c.lotId, uvlaka + 1, naPutu);
        naPutu.delete(c.lotId);
      }
    };
    hodaj(koren, 0, new Set([koren]));
    return out;
  }, [poRoditelju, koren]);

  if (redovi.length === 0) return null;

  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {redovi.map(({ cvor, uvlaka }, i) => (
        <li
          key={`${cvor.parentLotId}-${cvor.lotId}-${i}`}
          style={{ ...redStabla, paddingLeft: 12 + uvlaka * 20 }}
        >
          <span style={{ ...vrsta, ...bojaVrste(cvor.kind) }}>{imeVrste(cvor.kind, t)}</span>
          <b style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{cvor.designation ?? "—"}</b>
          <span style={imeRobe} title={cvor.productName ?? undefined}>{cvor.productName ?? "—"}</span>
          {/* Datum proizvodnje sirovine stoji uz sirovinu, a datum naloga uz nalog. Bez te razlike
              se datum naloga čita kao datum sirovine, pa izgleda da je sastojak napravljen posle
              robe u koju je ušao. */}
          {cvor.producedOn && (
            <span style={{ ...tiho, flexShrink: 0, fontSize: 11.5 }}>
              {t("trace.origin.madeOn", "proizveden {{datum}}", { datum: formatDate(cvor.producedOn) })}
            </span>
          )}
          <span style={{ flexShrink: 0, fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" }}>
            {kolicinaVeze(cvor.quantity, cvor.unit, locale)}
          </span>
          {cvor.documentNumber && (
            <span style={{ ...tiho, flexShrink: 0, fontSize: 11.5 }} title={cvor.recipe ?? undefined}>
              {t("trace.origin.byOrder", "po nalogu {{broj}}", { broj: cvor.documentNumber })}
              {cvor.documentDate
                ? ` ${t("trace.origin.orderDated", "od {{datum}}", { datum: formatDate(cvor.documentDate) })}`
                : ""}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Quantity on one origin link, with up to three decimals, so a micro-ingredient of a few grams reads
 * 0,004 kg rather than 0. A stored zero is a positive amount below the third decimal, since a true
 * zero arrives from the ERP as empty, the same as a missing quantity; empty stays a dash.
 */
function kolicinaVeze(n: number | null, jed: string | null, locale: string) {
  if (n == null) return "—";
  const broj = n === 0
    ? `< ${formatNumber(0.001, locale, { maximumFractionDigits: 3 })}`
    : formatNumber(n, locale, { maximumFractionDigits: 3 });
  return `${broj}${jed ? ` ${jed}` : ""}`;
}

function Odeljak({
  naslov, podnaslov, children,
}: {
  naslov: string;
  podnaslov?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={kartica}>
      <div>
        <h2 style={{ margin: 0, fontSize: 15 }}>{naslov}</h2>
        {podnaslov && (
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--ink-3)", maxWidth: 680 }}>
            {podnaslov}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

/** Prevođenje nije prerada, pa ni ne sme da izgleda isto. */
function imeVrste(k: OriginKind, t: (k: string, d: string) => string) {
  switch (k) {
    case "RADNI_NALOG": return t("trace.kind.workOrder", "radni nalog");
    case "POLUPROIZVOD": return t("trace.kind.semi", "poluproizvod");
    case "EKSTRAKCIJA": return t("trace.kind.extraction", "ekstrakcija");
    case "PREVODJENJE": return t("trace.kind.transfer", "prepisano");
  }
}

function bojaVrste(k: OriginKind): CSSProperties {
  return k === "PREVODJENJE"
    ? { color: "var(--ink-4)", borderColor: "var(--border)" }
    : { color: "var(--ink-3)", borderColor: "var(--border)" };
}

const traka: CSSProperties = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const polje: CSSProperties = {
  flex: 1, minWidth: 220, padding: "8px 10px", fontSize: 13,
  border: "1px solid var(--border)", borderRadius: "var(--r-sm, 8px)",
  background: "var(--surface)", color: "var(--ink)",
};
const kartica: CSSProperties = {
  border: "1px solid var(--border)", borderRadius: "var(--r-md, 10px)", padding: "14px 16px",
  display: "flex", flexDirection: "column", gap: 12,
};
const spisakNalaza: CSSProperties = {
  listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column",
  border: "1px solid var(--border)", borderRadius: "var(--r-md, 10px)", overflow: "hidden",
};
const nalaz: CSSProperties = {
  display: "flex", gap: 12, alignItems: "baseline", width: "100%", textAlign: "left",
  padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer",
  color: "var(--ink)", fontSize: 13,
};
const tabela: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--border)",
  color: "var(--ink-3)", fontWeight: 500, fontSize: 12,
};
const thD: CSSProperties = { ...th, textAlign: "right" };
const td: CSSProperties = { padding: "6px 8px", borderBottom: "1px solid var(--hover)" };
const tdD: CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const veza: CSSProperties = { color: "var(--brand-primary)", textDecoration: "none" };
const redStabla: CSSProperties = {
  display: "flex", gap: 10, alignItems: "baseline", padding: "4px 0", fontSize: 12.5,
  borderBottom: "1px solid var(--hover)",
};
const vrsta: CSSProperties = {
  fontSize: 10.5, border: "1px solid var(--border)", borderRadius: 999, padding: "1px 7px",
  flexShrink: 0, whiteSpace: "nowrap",
};
const imeRobe: CSSProperties = {
  flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  color: "var(--ink-2)",
};
const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5, margin: 0 };

export default TraceabilityPage;
